import React, { useEffect, useRef, useState } from 'react';
import { sanitizeMessageHtml } from './messageContent';

/**
 * Editor del testo di una comunicazione. Offre solo la formattazione che la
 * bacheca del portale sa mostrare (grassetto, corsivo, elenchi, citazione,
 * link HTTP(S)); a ogni modifica l'HTML viene ridotto a quel sottoinsieme.
 *
 * contentEditable + execCommand: deprecato sulla carta ma supportato da tutti
 * i browser, e sufficiente per una formattazione cosi' limitata.
 */
interface Props {
    value: string;
    onChange: (html: string) => void;
    id?: string;
    invalid?: boolean;
    placeholder?: string;
}

type Command = { cmd: string; arg?: string; icon: string; label: string; shortcut?: string };

const COMMANDS: Command[] = [
    { cmd: 'bold', icon: 'bi-type-bold', label: 'Grassetto', shortcut: 'Ctrl+B' },
    { cmd: 'italic', icon: 'bi-type-italic', label: 'Corsivo', shortcut: 'Ctrl+I' },
    { cmd: 'insertUnorderedList', icon: 'bi-list-ul', label: 'Elenco puntato' },
    { cmd: 'insertOrderedList', icon: 'bi-list-ol', label: 'Elenco numerato' },
    { cmd: 'formatBlock', arg: 'blockquote', icon: 'bi-quote', label: 'Citazione' },
];

const MessageEditor: React.FC<Props> = ({ value, onChange, id, invalid, placeholder }) => {
    const ref = useRef<HTMLDivElement>(null);
    const lastEmitted = useRef<string>('');
    const [linkOpen, setLinkOpen] = useState(false);
    const [linkUrl, setLinkUrl] = useState('https://');
    const savedRange = useRef<Range | null>(null);

    // Allinea il DOM solo quando il valore arriva da fuori (caricamento),
    // non a ogni battitura: altrimenti il cursore salterebbe all'inizio.
    useEffect(() => {
        if (ref.current && value !== lastEmitted.current) {
            ref.current.innerHTML = value;
            lastEmitted.current = value;
        }
    }, [value]);

    const emit = () => {
        if (!ref.current) return;
        const clean = sanitizeMessageHtml(ref.current.innerHTML);
        lastEmitted.current = clean;
        onChange(clean);
    };

    const run = (c: Command) => {
        ref.current?.focus();
        document.execCommand(c.cmd, false, c.arg);
        emit();
    };

    const openLink = () => {
        const sel = window.getSelection();
        savedRange.current = sel && sel.rangeCount > 0 ? sel.getRangeAt(0).cloneRange() : null;
        setLinkUrl('https://');
        setLinkOpen(true);
    };

    const applyLink = () => {
        const url = linkUrl.trim();
        setLinkOpen(false);
        if (!/^https?:\/\/\S+$/i.test(url)) return;
        ref.current?.focus();
        const sel = window.getSelection();
        if (savedRange.current && sel) {
            sel.removeAllRanges();
            sel.addRange(savedRange.current);
        }
        if (sel && sel.isCollapsed) {
            document.execCommand('insertHTML', false, `<a href="${url.replace(/"/g, '&quot;')}">${url.replace(/</g, '&lt;')}</a>`);
        } else {
            document.execCommand('createLink', false, url);
        }
        emit();
    };

    const onPaste = (e: React.ClipboardEvent) => {
        // Incolla come testo semplice: niente stili o markup da Word/web.
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain');
        document.execCommand('insertText', false, text);
        emit();
    };

    return (
        <div className={`msg-editor ${invalid ? 'is-invalid' : ''}`}>
            <div className="msg-editor__toolbar" role="toolbar" aria-label="Formattazione">
                {COMMANDS.map(c => (
                    <button
                        key={c.label}
                        type="button"
                        className="msg-editor__btn"
                        title={c.shortcut ? `${c.label} (${c.shortcut})` : c.label}
                        aria-label={c.label}
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => run(c)}>
                        <i className={`bi ${c.icon}`} />
                    </button>
                ))}
                <span className="msg-editor__sep" />
                <button type="button" className="msg-editor__btn" title="Link" aria-label="Inserisci link"
                    onMouseDown={e => e.preventDefault()} onClick={openLink}>
                    <i className="bi bi-link-45deg" />
                </button>
                <button type="button" className="msg-editor__btn" title="Rimuovi formattazione" aria-label="Rimuovi formattazione"
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => { ref.current?.focus(); document.execCommand('removeFormat'); document.execCommand('unlink'); emit(); }}>
                    <i className="bi bi-eraser" />
                </button>
                {linkOpen && (
                    <div className="msg-editor__link">
                        <input
                            className="form-control form-control-sm"
                            value={linkUrl}
                            autoFocus
                            aria-label="Indirizzo del link"
                            onChange={e => setLinkUrl(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Enter') { e.preventDefault(); applyLink(); }
                                if (e.key === 'Escape') setLinkOpen(false);
                            }}
                        />
                        <button type="button" className="btn btn-primary btn-sm" onClick={applyLink}
                            disabled={!/^https?:\/\/\S+$/i.test(linkUrl.trim())}>Inserisci</button>
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setLinkOpen(false)}>Annulla</button>
                    </div>
                )}
            </div>
            <div
                ref={ref}
                id={id}
                className="msg-editor__area"
                contentEditable
                role="textbox"
                aria-multiline="true"
                data-placeholder={placeholder}
                suppressContentEditableWarning
                onInput={emit}
                onBlur={emit}
                onPaste={onPaste}
            />
            <div className="msg-editor__hint">
                Solo link https://. Il testo incollato perde la formattazione originale.
            </div>
        </div>
    );
};

export default MessageEditor;
